/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import './AcOverAllUpTime.css';
import { IconWithTooltip } from '../../../globalUtils/globalIcons';
import { TableSubHeader, useAppDispatch, useAppSelector } from '../../../globalUtils/globalExports';
import { renderHeaderActionButtons } from '../../../globalUtils/globalHooks';
import { RectangularSkeleton } from '../../../globalUtils/SkeletonDesign/SkeletonDesign';
import { getReferenceDataReducer } from '../../../rmsReduxStore/reduxExports';
import { getAcDashboardReducer, getAcReferenceDataReducer } from '../../rmsReduxStore/acReduxExports';
import { getAcFilterSelectedValue, getCurrentAcFiltersValues, getCurrentScreenAppliedAcFiltersCount, setAcFilterData } from '../../rmsReduxStore/acFiltersRedux/acFiltersCreators';
import AcCustomFilterLayout, { AcCustomFilterProp } from './AcCustomFilterLayout';

interface AcOverallUptimeProp {
  handleFilter: Function,
  getFilterValue: Function
}

const OVERALL_UPTIME_FILTER_SCREEN = 'overallUptime'
const MAX_CHART_HEIGHT = 550
const navigationUpperLimit = 31

const AcOverAllUpTime: React.FC<AcOverallUptimeProp> = (props) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const chartBodyRef = useRef<HTMLDivElement>(null);
  const { handleFilter } = props;
  const dispatch = useAppDispatch();
  const { chargingStationReferenceData, chargerVisibleIdReferenceData } = useAppSelector(getAcReferenceDataReducer);
  const { intervalRangeReferenceData } = useAppSelector(getReferenceDataReducer);
  const { newDashboardOverallUptimeInfo } = useAppSelector(getAcDashboardReducer);
  const [visibleMin, setVisibleMin] = useState(0);
  const [visibleMax, setVisibleMax] = useState(navigationUpperLimit);
  const [chartHeight, setChartHeight] = useState(400);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const dataLength = newDashboardOverallUptimeInfo?.length || 0;

  // Keep the chart height in sync with the widget body
  useEffect(() => {
    if (!chartBodyRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setChartHeight(Math.min(entry.contentRect.height, MAX_CHART_HEIGHT));
      }
    });
    resizeObserver.observe(chartBodyRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  const xAxisLabelFormatter = function (this: Highcharts.AxisLabelsFormatterContextObject) {
    const label = typeof this.value === 'string' ? this.value : String(this.value ?? '');
    const [day, month] = label.split(' ');
    return `<div style="text-align: center;">${day}<br>${month}</div>`;
  };

  const getFormattedData = useCallback(() => {
    const visibleData = (newDashboardOverallUptimeInfo || []).slice(visibleMin, visibleMax + 1);
    return {
      xAxis: visibleData.map(item => item?.date),
      uptimeValues: visibleData.map(item => item?.data ?? 0),
    };
  }, [newDashboardOverallUptimeInfo, visibleMin, visibleMax]);

  const { xAxis, uptimeValues } = getFormattedData();

  let labelStep = 1;
  if (xAxis.length > 20) labelStep = 4;
  else if (xAxis.length > 10) labelStep = 2;

  const handleNavigation = (direction: 'left' | 'right') => {
    const step = navigationUpperLimit + 1;
    if (direction === 'left') {
      setVisibleMin(Math.max(visibleMin - step, 0));
      setVisibleMax(Math.max(visibleMax - step, step));
    } else {
      setVisibleMin(Math.min(visibleMin + step, dataLength - (step + 1)));
      setVisibleMax(Math.min(visibleMax + step, dataLength - 1));
    }
  };

  const handleChartPageReset = () => {
    setVisibleMin(0);
    setVisibleMax(navigationUpperLimit);
  }

  const options: Highcharts.Options = {
    chart: {
      type: 'column',
      backgroundColor: 'transparent',
      height: chartHeight,
      spacing: [15, 10, 10, 10],
      events: {
        render: function () {
          const chart = this as Highcharts.Chart & any;

          // Remove existing custom arrows before adding new ones
          const existingArrows = chart.container.getElementsByClassName('custom-arrow');
          while (existingArrows.length > 0) {
            existingArrows[0].remove();
          }

          if (visibleMin > 0) {
            chart.renderer.image('/images/left_arrow_highchart.svg', chart.plotLeft, chart.plotTop + chart.plotHeight / 2 - 10, 20, 20)
              .addClass('custom-arrow left')
              .on('click', () => handleNavigation('left'))
              .add();
          }

          if (visibleMax < dataLength - 1) {
            chart.renderer.image('/images/right_arrow_highchart.svg', chart.plotLeft + chart.plotWidth - 15, chart.plotTop + chart.plotHeight / 2 - 10, 20, 20)
              .addClass('custom-arrow right')
              .on('click', () => handleNavigation('right'))
              .add();
          }
        }
      },
    },
    title: { text: undefined },
    subtitle: { text: undefined },
    xAxis: {
      categories: xAxis,
      lineColor: '#005EFF',
      tickColor: '#005EFF',
      gridLineWidth: dataLength > 10 ? 0 : 1,
      gridLineDashStyle: 'ShortDash',
      plotBands: xAxis.map((_, index) => {
        const barWidthFraction = 0.25;
        return {
          from: index - barWidthFraction,
          to: index + barWidthFraction,
          color: '#f5f5f5'
        };
      }),
      labels: {
        rotation: 0,
        step: labelStep,
        formatter: xAxisLabelFormatter,
      }
    },
    yAxis: {
      title: { text: 'Percentage' },
      gridLineDashStyle: 'ShortDash',
    },
    tooltip: {
      shared: true,
    },
    legend: {
      align: 'center',
      verticalAlign: 'bottom',
      enabled: true,
      useHTML: true,
      labelFormatter: function () {
        const isVisible = this.visible !== false;
        const textStyle = isVisible ? '' : 'color: #999; text-decoration: line-through;';
        const boxColor = isVisible ? this.color : '#ccc';

        return `
          <span style="display: inline-flex; align-items: center; gap: 6px; ${textStyle}">
            <span style="width: 10px; height: 10px; background-color: ${boxColor}; display: inline-block; border-radius: 50%;"></span>
            <span>${this.name}</span>
          </span>
        `;
      },
      itemStyle: {
        fontSize: '12px',
        cursor: 'default',
      },
      symbolWidth: 0,
      symbolHeight: 0,
      symbolPadding: 0,
      symbolRadius: 0
    },
    plotOptions: {
      column: {
        borderRadius: 8,
        pointPadding: 0.2,
        groupPadding: 0.1,
      },
      series: {
        marker: {
          enabled: true,
        },
      },
    },
    series: [
      {
        name: 'Uptime',
        type: 'column',
        data: uptimeValues,
        color: 'var(--secondary-blue-color)',
      },
    ],
    credits: { enabled: false },
    exporting: { enabled: false },
  };

  const headerActionBtns = renderHeaderActionButtons({
    actionBtns: [
      {
        id: 'add_filter_btn',
        isAccessible: true,
        headerActionBtnClick: (e): void => {
          setFilterAnchorEl(e.currentTarget)
        },
        badgeCount: getCurrentScreenAppliedAcFiltersCount(OVERALL_UPTIME_FILTER_SCREEN, true)
      },
    ],
  })

  const handleFilterDataChange = async (val, filterKey, isGlobal) => {
    const res = await setAcFilterData(val, filterKey, OVERALL_UPTIME_FILTER_SCREEN, 'SET', isGlobal ? isGlobal : false, true)(dispatch)
    const dashboardV2Filters = getCurrentAcFiltersValues('acdashboard')

    if (res?.message === 'Action dispatched successfully') {
      handleFilter(dashboardV2Filters, res?.filters)
      handleChartPageReset();
    }
  }

  const overallUptimeFilterProps: AcCustomFilterProp[] = useMemo(() => {
    return [
      {
        filterLabel: 'Charging Stations',
        filterType: 'dropdown',
        filterId: 'chargingStationId',
        filterDropdownProps: {
          selectValue: getAcFilterSelectedValue('chargingStationId', OVERALL_UPTIME_FILTER_SCREEN, false),
          selectOptions: chargingStationReferenceData ?? [],
          handleSelectOptionChange: (val): void => {
            handleFilterDataChange(val, 'chargingStationId', false)
          },
          selectDropDownId: 'ac-overall-uptime-charging-station-filter-dropdown',
          isMultiSelect: true,
          renderToBody: true,
        }
      },
      {
        filterLabel: 'Chargers',
        filterId: 'chargerId',
        filterType: 'dropdown',
        filterDropdownProps: {
          selectValue: getAcFilterSelectedValue('chargerId', OVERALL_UPTIME_FILTER_SCREEN, false),
          selectOptions: chargerVisibleIdReferenceData ?? [],
          handleSelectOptionChange: (val): void => {
            handleFilterDataChange(val, 'chargerId', false)
          },
          selectDropDownId: 'ac-overall-uptime-charger-id-filter-dropdown',
          isMultiSelect: true,
          renderToBody: true
        }
      },
      {
        filterLabel: 'Date Range',
        filterId: 'uptimeIntervalRange',
        filterType: 'dropdown',
        filterDropdownProps: {
          selectValue: getAcFilterSelectedValue('uptimeIntervalRange', OVERALL_UPTIME_FILTER_SCREEN, false),
          selectOptions: intervalRangeReferenceData ?? [],
          handleSelectOptionChange: (val): void => {
            handleFilterDataChange(val, 'uptimeIntervalRange', false)
          },
          selectDropDownId: 'ac-overall-uptime-date-range-filter-dropdown',
          isMultiSelect: false,
          renderToBody: true
        }
      },
    ]
  }, [intervalRangeReferenceData, chargerVisibleIdReferenceData, chargingStationReferenceData, handleFilterDataChange, getAcFilterSelectedValue])

  return (
    <div className="widget-wrap overall-uptime-wrap">
      <div className="widget-header chart-header">
        <div className='widget-heading overall-uptime-header'>
          Overall Uptime of Chargers
          <IconWithTooltip iconClassName="info-icon" toolTipText={'Uptime, Online and Offline chargers, etc.'} containerClassName={'new-dashboard-widget-header-tooltip'} />
        </div>
        <div className="chart-filters">
          <TableSubHeader headerActionBtns={headerActionBtns} />
          <AcCustomFilterLayout filters={overallUptimeFilterProps} requiredReferenceDataCallbacks={['chargerVisibleId', 'chargingStationId']} filterAnchorEl={filterAnchorEl} setFilterAnchorEl={setFilterAnchorEl} excludeGlobalFilters={true} />
        </div>
      </div>
      <div ref={chartBodyRef} className='widget-body ac-overall-uptime-body'>
        {!newDashboardOverallUptimeInfo ?
          <div style={{ height: `${chartHeight}px` }}>
            <RectangularSkeleton skeletonContainerClassName='' />
          </div>
          :
          <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
        }
      </div>
    </div>
  );
};

export default AcOverAllUpTime;
